/* ═══════════════════════════════════════════════════════
   Monmouth Cards — Collection Page Enhancements
   assets/monmouth-collection-enhance.js

   a) Default-to-Newest redirect. Now an inline script at the top of
      sections/monmouth-collection-filters.liquid (runs before first paint);
      nothing left to do here.

   b) ONE page bar for every collection, with a products-per-page selector
      (24/48/72, remembered in localStorage).

      How the products reach the browser: the section renders in-stock cards only
      (sold-out products are skipped AFTER Shopify cuts a page, so a server page
      holds a varying number of cards). A collection of up to 250 products is sent
      whole, in one server page. A bigger collection is sent in server pages
      ("batches") of 125 raw products; the grid's data-total-pages is the number of
      batches and data-current-page the one in the document.

      The page bar always counts IN-STOCK products across the whole collection:
      page j is products [(j-1)*size, j*size) of one continuous list. For a big
      collection the in-stock number of every batch is read from the tiny helper
      section sections/monmouth-collection-counts.liquid (250 raw products per
      request = two batches), so batch boundaries are known exactly and the numbers
      never restart. A page that crosses a batch boundary is completed with the
      neighbouring batch's cards, fetched with the same Sections Rendering API the
      filters use, and appended/prepended to the grid.

      URL: ?page=N is the Shopify batch that holds the FIRST product of the page
      (what the server needs to render) and ?cp=J is the page number the customer
      sees. Collections with a single batch never have ?page=, only ?cp=J (as
      before). A grid that was changed by the filter engine (Great Deal scan,
      OR-merge) is paged locally over the cards it holds.
   ═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* Must match SECTION_ID in monmouth-collection.js — that constant is
     module-private there (not exposed on window.MC), so it's duplicated
     here rather than invented differently. */
  var SECTION_ID = 'monmouth-collection-filters';
  var COUNTS_SECTION_ID = 'monmouth-collection-counts';
  var STORAGE_KEY = 'mc-products-per-page';
  var PER_PAGE_SIZE_OPTIONS = [24, 48, 72];
  /* Raw products per Shopify page for collections above 250 products: must equal buffer_size in the section and the
     split in the counts section (data-chunk-size on the grid says which one rendered this page). */
  var CHUNK_RAW = 125;
  var COUNT_CACHE_MS = 10 * 60 * 1000;
  var MAX_COUNT_REQUESTS = 40;
  var COUNT_PARALLEL = 3;
  var WAIT_LIMIT_MS = 6000;
  var BATCH_TIMEOUT_MS = 12000;

  /* ══════════════════════════════════════════════════════
     a) DEFAULT-TO-NEWEST REDIRECT (now inline in the section)
  ══════════════════════════════════════════════════════ */
  function maybeRedirectToNewestDefault() {}

  /* Storage can throw (blocked / private mode): shared safe helper (assets/monmouth-safe-storage.js), harmless fallback if absent. */
  function storeGet(k, kind) { try { return window.mcStorage ? window.mcStorage.get(k, kind) : (kind === 'session' ? sessionStorage : localStorage).getItem(k); } catch (e) { return null; } }
  function storeSet(k, v, kind) { try { if (window.mcStorage) window.mcStorage.set(k, v, kind); else (kind === 'session' ? sessionStorage : localStorage).setItem(k, v); } catch (e) { /* not remembered */ } }
  function storeRemove(k) { try { if (window.mcStorage) window.mcStorage.remove(k); else localStorage.removeItem(k); } catch (e) { /* no-op */ } }

  function getSize(grid) {
    var def = parseInt(grid.getAttribute('data-per-page'), 10) || 24;
    var saved = parseInt(storeGet(STORAGE_KEY), 10);
    return PER_PAGE_SIZE_OPTIONS.indexOf(saved) >= 0 ? saved : def;
  }

  function announce(msg) { try { if (window.mcAnnounce) window.mcAnnounce(msg); } catch (e) { /* no-op */ } }

  /* ══════════════════════════════════════════════════════
     URL helpers
  ══════════════════════════════════════════════════════ */
  function urlCp() {
    var n = parseInt(new URL(window.location.href).searchParams.get('cp'), 10);
    return n && n > 0 ? n : 0;
  }

  /* Address for page j. chunk = Shopify batch holding the page's first product (null: leave ?page= alone). */
  function urlFor(j, chunk) {
    var url = new URL(window.location.href);
    if (chunk !== null && chunk !== undefined) {
      if (chunk > 1) url.searchParams.set('page', String(chunk)); else url.searchParams.delete('page');
    }
    if (j > 1) url.searchParams.set('cp', String(j)); else url.searchParams.delete('cp');
    return url.toString();
  }

  /* Same collection / filters / sort, any other batch, as a Sections Rendering API request. */
  function sectionUrl(sectionId, page) {
    var url = new URL(window.location.href);
    url.searchParams.delete('cp');
    url.searchParams.delete('sections');
    if (page > 1) url.searchParams.set('page', String(page)); else url.searchParams.delete('page');
    url.searchParams.set('sections', sectionId);
    return url.pathname + url.search;
  }

  /* Identifies "the same list of products" for the counts cache: path + every parameter except the page ones. */
  function countKey() {
    var url = new URL(window.location.href);
    var parts = [];
    url.searchParams.forEach(function (v, k) {
      if (k === 'page' || k === 'cp' || k === 'sections' || k === 'great_deal' || k === 'mc_or') return;
      parts.push(k + '=' + v);
    });
    return url.pathname + '?' + parts.join('&');
  }

  function fetchSectionJson(url, id) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    }).then(function (data) {
      if (!data || !data[id]) throw new Error('Section not found');
      return data[id];
    });
  }

  /* ══════════════════════════════════════════════════════
     VIEW — what is in the grid right now
     mode 'chunk': the grid still holds exactly the cards of Shopify batch(es) [lo..hi] of a multi-batch collection.
     mode 'local': single batch, or a grid the filter engine changed — paged over whatever cards it holds.
  ══════════════════════════════════════════════════════ */
  var view = null;
  var countEntries = {};

  function initView(grid) {
    var T = parseInt(grid.getAttribute('data-total-pages'), 10) || 1;
    var P = parseInt(grid.getAttribute('data-current-page'), 10) || 1;
    var B = parseInt(grid.getAttribute('data-chunk-size'), 10) || 0;
    var params = new URL(window.location.href).searchParams;
    var chunked = T > 1 && B === CHUNK_RAW && !params.has('great_deal') && !params.has('mc_or');
    var nodes = Array.prototype.slice.call(grid.children);
    view = {
      grid: grid, mode: chunked ? 'chunk' : 'local', T: T, P: P, lo: P, hi: P,
      nodes: nodes, raw: parseInt(grid.getAttribute('data-raw-total'), 10) || 0,
      size: 24, curJ: 1, waiting: false, waitTimer: null, loading: false, busy: false, painted: false, failed: {}, entry: null, key: null
    };
    return view;
  }

  function ensureView(grid) {
    if (!view || view.grid !== grid || grid.hasAttribute('data-paged')) initView(grid);
    return view;
  }

  /* True while the grid's children are still exactly the cards this module loaded (nobody merged or filtered them). */
  function stillOurs(v) {
    var kids = v.grid.children;
    if (kids.length !== v.nodes.length) return false;
    for (var i = 0; i < kids.length; i++) if (kids[i] !== v.nodes[i]) return false;
    return true;
  }

  /* ══════════════════════════════════════════════════════
     COUNTS — in-stock products per batch
  ══════════════════════════════════════════════════════ */
  function getEntry(v) {
    var key = countKey();
    var e = countEntries[key];
    if (!e || e.T !== v.T || e.raw !== v.raw) {
      e = countEntries[key] = { key: key, T: v.T, raw: v.raw, n: {}, actual: {}, started: false, done: false, failed: false, settled: false };
      try {
        var cached = JSON.parse(storeGet('mc-chunk-counts:' + key, 'session') || 'null');
        if (cached && cached.T === v.T && cached.raw === v.raw && Date.now() - cached.ts < COUNT_CACHE_MS && cached.n && cached.n.length === v.T) {
          for (var i = 0; i < cached.n.length; i++) e.n[i + 1] = cached.n[i];
          e.started = true; e.done = true; e.settled = true;
        }
      } catch (err) { /* no cache */ }
    }
    v.entry = e; v.key = key;
    return e;
  }

  function startCounts(v) {
    var e = getEntry(v);
    if (e.started) return;
    e.started = true;
    var pages = Math.min(Math.ceil(v.T / 2), MAX_COUNT_REQUESTS);
    var next = 1, running = 0, finished = 0;
    function finish() {
      e.settled = true;
      var known = 0, q;
      for (q = 1; q <= e.T; q++) if (e.n[q] !== undefined) known++;
      e.done = known === e.T;
      if (e.done && !e.failed) {
        var arr = [];
        for (q = 1; q <= e.T; q++) arr.push(e.n[q]);
        storeSet('mc-chunk-counts:' + e.key, JSON.stringify({ ts: Date.now(), T: e.T, raw: e.raw, n: arr }), 'session');
      }
      onCountsChanged(e);
    }
    function pump() {
      while (running < COUNT_PARALLEL && next <= pages) {
        (function (k) {
          running++;
          function ask() { return fetchSectionJson(sectionUrl(COUNTS_SECTION_ID, k), COUNTS_SECTION_ID); }
          ask().catch(function () { return ask(); }).then(function (html) {
            var el = new DOMParser().parseFromString(html, 'text/html').getElementById('mc-chunk-counts');
            if (!el) throw new Error('no counts');
            var first = (k - 1) * 2 + 1;
            var n1 = parseInt(el.getAttribute('data-n1'), 10), n2 = parseInt(el.getAttribute('data-n2'), 10);
            var raw = parseInt(el.getAttribute('data-raw'), 10);
            if (!e.actual[first] && !isNaN(n1)) e.n[first] = n1;
            if (first + 1 <= e.T) {
              if (!e.actual[first + 1] && !isNaN(n2)) e.n[first + 1] = n2;
            }
            if (raw === 0 || isNaN(raw)) e.failed = true;
          }).catch(function () { e.failed = true; }).then(function () {
            running--; finished++;
            if (finished === pages) finish(); else { onCountsChanged(e); pump(); }
          });
        })(next++);
      }
    }
    pump();
  }

  function onCountsChanged(e) {
    if (!view || view.entry !== e || view.mode !== 'chunk') return;
    applyPaging(false);
  }

  /* In-stock products before batch q (1-based), or null while a count before it is unknown. */
  function offsetOf(v, q) {
    var e = v.entry, off = 0;
    for (var i = 1; i < q; i++) {
      if (e.n[i] === undefined) return null;
      off += e.n[i];
    }
    return off;
  }

  /* { sum, known (contiguous from batch 1), complete } */
  function totalInfo(v) {
    var e = v.entry, sum = 0, i = 1;
    while (i <= v.T && e.n[i] !== undefined) { sum += e.n[i]; i++; }
    return { sum: sum, known: i - 1, complete: (i - 1) === v.T };
  }

  /* Batch that holds in-stock product number a (0-based). */
  function chunkOfIndex(v, a) {
    var e = v.entry, off = 0;
    for (var q = 1; q <= v.T; q++) {
      if (e.n[q] === undefined) return q;
      if (a < off + e.n[q]) return q;
      off += e.n[q];
    }
    return v.T;
  }

  /* ══════════════════════════════════════════════════════
     PAGE BAR
  ══════════════════════════════════════════════════════ */
  function pageList(cur, total, more) {
    var out = [], i;
    if (!more && total <= 7) {
      for (i = 1; i <= total; i++) out.push(i);
      return out;
    }
    var set = {};
    function add(n) { if (n >= 1 && n <= total) set[n] = true; }
    add(1);
    if (!more) add(total);
    if (cur <= 4) { for (i = 1; i <= 5; i++) add(i); }
    else if (!more && cur >= total - 3) { for (i = total - 4; i <= total; i++) add(i); }
    else { add(cur - 1); add(cur); add(cur + 1); }
    var nums = Object.keys(set).map(Number).sort(function (a, b) { return a - b; });
    var prev = 0;
    nums.forEach(function (n) {
      if (n - prev === 2) out.push(prev + 1);
      else if (n - prev > 2) out.push('gap');
      out.push(n);
      prev = n;
    });
    if (more) out.push('gap');
    return out;
  }

  var CHEV = function (d) { return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="width:14px;height:14px" aria-hidden="true" focusable="false"><polyline points="' + d + '"/></svg>'; };

  /* link(j) -> { href, chunk } for page j. */
  function renderPager(grid, cur, total, more, link) {
    var old = document.getElementById('mc-client-pager');
    if (total <= 1 && !more) { if (old) old.remove(); return; }
    var html = '<nav class="mc-pagination" id="mc-client-pager" aria-label="Pagination"><div class="mc-pagination__nav">';
    var l;
    if (cur > 1) {
      l = link(cur - 1);
      html += '<a href="' + l.href + '" data-pg="' + (cur - 1) + '" data-chunk="' + (l.chunk || '') + '" rel="prev" aria-label="Previous page" class="mc-pagination__btn mc-pagination__btn--prev">' + CHEV('15 18 9 12 15 6') + ' Prev</a>';
    }
    html += '<span class="mc-pagination__pages">';
    pageList(cur, total, more).forEach(function (n) {
      if (n === 'gap') { html += '<span class="mc-pagination__page mc-pagination__page--gap" aria-hidden="true">…</span>'; return; }
      if (n === cur) { html += '<span class="mc-pagination__page mc-pagination__page--current" aria-current="page" aria-label="Page ' + n + ', current page">' + n + '</span>'; return; }
      l = link(n);
      html += '<a href="' + l.href + '" data-pg="' + n + '" data-chunk="' + (l.chunk || '') + '" aria-label="Page ' + n + '" class="mc-pagination__page">' + n + '</a>';
    });
    html += '</span>';
    if (cur < total || more) {
      l = link(cur + 1);
      html += '<a href="' + l.href + '" data-pg="' + (cur + 1) + '" data-chunk="' + (l.chunk || '') + '" rel="next" aria-label="Next page" class="mc-pagination__btn mc-pagination__btn--next">Next ' + CHEV('9 18 15 12 9 6') + '</a>';
    }
    html += '</div></nav>';
    if (old && old.getAttribute('data-sig') === html && old.isConnected) { return; }
    if (old) old.remove();
    var holder = document.createElement('div');
    holder.innerHTML = html;
    var nav = holder.firstChild;
    nav.setAttribute('data-sig', html);
    var anchor = document.querySelector('.mc-pagination--server') || grid;
    anchor.parentNode.insertBefore(nav, anchor.nextSibling);
    nav.addEventListener('click', onPagerClick);
  }

  function onPagerClick(e) {
    var a = e.target.closest ? e.target.closest('a[data-pg]') : null;
    if (!a) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return; // new tab / window: normal navigation
    var q = parseInt(a.getAttribute('data-chunk'), 10) || 0;
    if (view && view.mode === 'chunk' && q && (q < view.lo - 1 || q > view.hi + 1)) {
      /* a jump to a batch that is not next to the ones in the document: load it the normal way (the link is the real
         address). The neighbouring batch is fetched in place by chunkPaging instead. */
      return;
    }
    e.preventDefault();
    history.pushState({}, '', a.href);
    applyPaging(true);
  }

  /* ══════════════════════════════════════════════════════
     LOCAL MODE — page over the cards the grid holds
  ══════════════════════════════════════════════════════ */
  function localPaging(v, S, scroll) {
    var grid = v.grid;
    var cards = grid.children;
    var total = Math.max(1, Math.ceil(cards.length / S));
    var cur = Math.min(urlCp() || 1, total);
    for (var i = 0; i < cards.length; i++) {
      cards[i].style.display = (i >= (cur - 1) * S && i < cur * S) ? '' : 'none';
    }
    v.curJ = cur; v.size = S;
    renderPager(grid, cur, total, false, function (j) { return { href: urlFor(j, null), chunk: 0 }; });
    if (scroll) { scrollToGrid(grid); announce('Page ' + cur + ' of ' + total); }
  }

  function scrollToGrid(grid) {
    var top = grid.getBoundingClientRect().top + window.pageYOffset - 140;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /* ══════════════════════════════════════════════════════
     CHUNK MODE — continuous pages over several Shopify batches
  ══════════════════════════════════════════════════════ */
  function setWaiting(v, on) {
    if (on && !v.waiting) {
      v.waiting = true;
      v.grid.style.visibility = 'hidden';
      v.waitTimer = setTimeout(function () {
        /* the counts did not arrive: fall back to paging over this batch only (numbers restart per batch, as before) */
        if (view === v && v.waiting) { v.mode = 'local'; setWaiting(v, false); setBusy(v, false); applyPaging(false); }
      }, WAIT_LIMIT_MS);
    } else if (!on && v.waiting) {
      v.waiting = false;
      if (!v.busy || v.painted) v.grid.style.visibility = '';
      clearTimeout(v.waitTimer);
    }
  }

  function importCards(html) {
    var g = new DOMParser().parseFromString(html, 'text/html').getElementById('mc-product-grid');
    return g ? Array.prototype.map.call(g.children, function (c) { return document.importNode(c, true); }) : [];
  }

  function loadBatch(v, q) {
    var request = fetchSectionJson(sectionUrl(SECTION_ID, q), SECTION_ID).then(importCards).catch(function () { return null; });
    var giveUp = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, BATCH_TIMEOUT_MS); });
    return Promise.race([request, giveUp]);
  }

  function chunkPaging(v, S, scroll) {
    var e = getEntry(v);
    var grid = v.grid;
    /* the batch in the document is exact: its real card count beats the helper's number */
    if (v.lo === v.hi && !e.actual[v.P]) { e.n[v.P] = v.nodes.length; e.actual[v.P] = true; }
    startCounts(v);

    /* base = in-stock products before the first card in the grid; the page of a link without ?cp= is the one that
       holds the first product of the batch named by ?page= */
    var cp = urlCp();
    var urlBatch = parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1;
    var base = offsetOf(v, v.lo);
    var anchor = cp ? 0 : offsetOf(v, Math.min(urlBatch, v.T));
    if (base === null || anchor === null) {
      if (e.settled) { v.mode = 'local'; setWaiting(v, false); setBusy(v, false); localPaging(v, S, scroll); return; }
      setWaiting(v, true);
      return;
    }
    setWaiting(v, false);

    var info = totalInfo(v);
    var cur = cp || (Math.floor(anchor / S) + 1);
    var pages = Math.max(1, Math.ceil(info.sum / S));
    if (info.complete && cur > pages) cur = pages;
    if (cur < 1) cur = 1;
    v.curJ = cur; v.size = S;

    var a = (cur - 1) * S, b = a + S;
    if (info.complete && b > info.sum) b = info.sum;

    /* Does this page need cards from a batch that is not in the document yet (it crosses a batch boundary)? Then keep what
       is on screen (dimmed) until that batch has arrived, instead of showing a short page for a moment. */
    var end = base + v.nodes.length;
    var needBefore = a < base && v.lo > 1 && !v.failed[v.lo - 1];
    var needAfter = b > end && v.hi < v.T && !v.failed[v.hi + 1];
    if (!needBefore && !needAfter) { paintChunk(v, base, a, b); setBusy(v, false); v.painted = true; }
    else setBusy(v, true);

    var more = !info.complete;
    var lower = Math.max(cur, Math.ceil(info.sum / S));
    renderPager(grid, cur, more ? lower : pages, more, function (j) {
      var q = chunkOfIndex(v, (j - 1) * S);
      return { href: urlFor(j, q), chunk: q };
    });
    if (scroll) { scrollToGrid(grid); announce('Page ' + cur + (info.complete ? ' of ' + pages : '')); }

    /* fetch the missing neighbouring batch (one request at a time); when the page is complete, quietly fetch the next
       batch ahead of time once the customer is within two pages of the end of what is loaded */
    if (v.loading) return;
    var q = 0, before = false;
    if (needBefore) { q = v.lo - 1; before = true; }
    else if (needAfter) { q = v.hi + 1; }
    else if (v.hi < v.T && !v.failed[v.hi + 1] && end - b <= 2 * S) { q = v.hi + 1; }
    if (!q) return;
    v.loading = true;
    loadBatch(v, q).then(function (cards) {
      v.loading = false;
      if (view !== v || !document.body.contains(grid)) return;
      if (!cards) { v.failed[q] = true; applyPaging(false); return; }
      e.n[q] = cards.length; e.actual[q] = true;
      if (before) {
        var first = v.nodes.length ? v.nodes[0] : null;
        cards.forEach(function (c) { grid.insertBefore(c, first); });
        v.nodes = cards.concat(v.nodes);
        v.lo = q;
      } else {
        cards.forEach(function (c) { grid.appendChild(c); });
        v.nodes = v.nodes.concat(cards);
        v.hi = q;
      }
      applyPaging(false);
    });
  }

  /* While a neighbouring batch is on its way: first page of this document -> hidden until it can be drawn complete;
     otherwise the page on screen stays, slightly dimmed. */
  function setBusy(v, on) {
    var g = v.grid;
    if (on) {
      g.setAttribute('aria-busy', 'true');
      if (v.painted) g.style.opacity = '0.55'; else g.style.visibility = 'hidden';
      v.busy = true;
    } else if (v.busy) {
      v.busy = false;
      g.removeAttribute('aria-busy');
      g.style.opacity = '';
      if (!v.waiting) g.style.visibility = '';
    }
  }

  function paintChunk(v, base, a, b) {
    for (var i = 0; i < v.nodes.length; i++) {
      var p = base + i;
      v.nodes[i].style.display = (p >= a && p < b) ? '' : 'none';
    }
  }

  /* ══════════════════════════════════════════════════════
     ENTRY POINT
  ══════════════════════════════════════════════════════ */
  function applyPaging(scroll) {
    var grid = document.getElementById('mc-product-grid');
    if (!grid) return;
    var v = ensureView(grid);
    grid.removeAttribute('data-paged');
    var S = getSize(grid);
    var select = document.getElementById('mc-per-page-select');
    if (select) select.value = String(S);
    var server = document.querySelector('.mc-pagination--server');
    if (server) server.style.display = 'none';
    var oldBtn = document.getElementById('mc-load-more-btn');
    if (oldBtn) oldBtn.remove();

    if (v.mode === 'chunk' && !stillOurs(v)) { v.mode = 'local'; setWaiting(v, false); setBusy(v, false); }
    if (v.mode === 'chunk') chunkPaging(v, S, scroll);
    else localPaging(v, S, scroll);
  }

  function initPerPageControl() {
    var select = document.getElementById('mc-per-page-select');
    if (select && !select._mcBound) {
      select._mcBound = true;
      select.addEventListener('change', function () {
        var val = parseInt(this.value, 10);
        var grid = document.getElementById('mc-product-grid');
        var def = grid ? (parseInt(grid.getAttribute('data-per-page'), 10) || 24) : 24;
        /* keep the customer at the same products: the new page is the one that holds the first product now on screen */
        var first = view && view.grid === grid ? (view.curJ - 1) * view.size : 0;
        if (val === def) storeRemove(STORAGE_KEY); else storeSet(STORAGE_KEY, String(val));
        var j = Math.floor(first / val) + 1;
        var q = (view && view.mode === 'chunk' && view.entry) ? chunkOfIndex(view, (j - 1) * val) : null;
        history.replaceState({}, '', urlFor(j, q));
        applyPaging(false);
      });
    }
    var g = document.getElementById('mc-product-grid');
    if (g && g.hasAttribute('data-paged')) applyPaging(false);
  }

  window.addEventListener('popstate', function () { applyPaging(false); });

  /* Lets monmouth-collection.js re-page the grid after it adds cards to it (the "Great Deal" scan of later pages). */
  window.mcRepageGrid = function () { applyPaging(false); };

  /* Re-apply after monmouth-collection.js's fetchAndReplace swaps the
     section for a filter/sort change: the new grid is a fresh node
     (data-paged again) and the new select has no listener yet. */
  function observeSectionSwaps() {
    var section = document.querySelector('.mc-collection-section');
    if (!section || !window.MutationObserver) return;
    var mo = new MutationObserver(function () {
      initPerPageControl();
    });
    mo.observe(section, { childList: true });
  }

  maybeRedirectToNewestDefault();
  initPerPageControl();
  observeSectionSwaps();
})();
